import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useFixRuleQuery } from './useFixRuleQuery';
import { useFixRuleTableState } from './useFixRuleTableState';
import moment from 'moment';

export const useFixRuleTable = () => {
    const { t } = useTranslation();
    const { rows, setRows, loading, setLoading, loadData } = useFixRuleQuery();
    const { contextMenu, setContextMenu, selectedRow, setSelectedRow, selected, setSelected, cleanSelected } = useFixRuleTableState();

    const types_period = useMemo(
        () => [
            { value: 'day', label: t('settings.fixed_rules.dialog.inputs.days') },
            { value: 'bimonthly', label: t('settings.fixed_rules.dialog.inputs.bimonthly') },
            { value: 'monthly', label: t('settings.fixed_rules.dialog.inputs.monthly') },
        ],
        [t],
    );

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'name',
                headerName: t('settings.fixed_rules.table.columns.name'),
                flex: 1,
            },
            {
                field: 'type_payment_period',
                headerName: t('settings.fixed_rules.table.columns.type_payment_period'),
                flex: 1,
                filterable: false,
                renderCell: (params: any) => {
                    const value = params.value;
                    const type = types_period.find((type: { value: string; label: string }) => type.value === value);
                    return type ? type.label : '—';
                },
            },
            {
                field: 'day_division_hours',
                headerName: t('settings.fixed_rules.table.columns.day_division_hours'),
                flex: 1,
                filterable: false,
                renderCell: (params: any) => {
                    const value = params.value;
                    const formatted = moment(value, 'HH:mm:ss', true).isValid() ? moment(value, 'HH:mm:ss').format('HH:mm') : '—';

                    return formatted;
                },
            },
            {
                field: 'hours_belong_to',
                headerName: t('settings.fixed_rules.table.columns.hours_belong_to'),
                flex: 1,
                filterable: false,
            },
        ],
        [t, types_period],
    );

    const reloading = useCallback(() => {
        const permissions = JSON.parse(localStorage.getItem('permissions') || '[]') as string[];
        const canReadFixedRules = permissions.includes('timekeeper.FixedRule');

        if (canReadFixedRules) {
            loadData().finally(() => {
                setContextMenu(null);
                cleanSelected();
            });
        } else {
            setRows([]);
            setLoading(false);
        }
    }, [cleanSelected, loadData, setContextMenu, setLoading, setRows]);

    return {
        rows,
        loading,
        loadData,
        reloading,
        selectedRow,
        setSelectedRow,
        contextMenu,
        setContextMenu,
        selected,
        setSelected,
        columns,
        cleanSelected,
    };
};
